// src/screens/PhrasesPractice.jsx
import React, { useEffect, useState, useMemo } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import {
  loadPhraseProgress,
  savePhraseProgress,
} from "../data/phraseProgressStorage";
import { loadPhrases } from "../data/loadPhrases";
import { languageLabel, parseLanguageId } from "../data/languages";
import {
  getPhrasesPerWeek,
  getPhraseWeekPlan,
} from "../data/phraseSchedule";
import TopicPractice from "./TopicPractice";
import {
  Badge,
  Button,
  Note,
  OptionCard,
  Panel,
  ScreenHeader,
  Stack,
  TextInput,
  Tile,
  TileTray,
} from "../components/ui";

// einfache Shuffle-Funktion
function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Eingaben tolerant vergleichen
function normalizePhrase(str) {
  return (str || "")
    .toLowerCase()
    .replace(/[’‘`´]/g, "'") // Apostroph vom iPhone
    .normalize("NFD") // Akzente entfernen
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[¿¡?!.,]/g, "") // Satzzeichen ignorieren
    .replace(/\s+/g, " ")
    .trim();
}

// Stage-Ranking: niedrigere Zahl = früheres Stadium
function stageRank(stage) {
  if (stage === "done") return 4;
  if (stage === 3) return 3;
  if (stage === 2) return 2;
  return 1; // default/Stage 1
}

// Nächste Redewendung auswählen:
// - Nur nicht fertige ("done") Phrasen
// - Möglichst nicht dieselbe wie lastIndex
// - Bevorzugt Phrasen mit niedrigster Stage (1 -> 2 -> 3)
function pickNextPhraseIndex(states, lastIndex) {
  if (!states || states.length === 0) return null;

  const nonDoneIndices = states
    .map((st, idx) => ({ st, idx }))
    .filter(({ st }) => st && st.stage !== "done")
    .map(({ idx }) => idx);

  if (nonDoneIndices.length === 0) return null;

  let pool = nonDoneIndices;
  if (lastIndex != null) {
    const withoutLast = pool.filter((i) => i !== lastIndex);
    if (withoutLast.length > 0) {
      pool = withoutLast;
    }
  }

  let minStageRank = Infinity;
  pool.forEach((i) => {
    const r = stageRank(states[i]?.stage);
    if (r < minStageRank) minStageRank = r;
  });

  let phasePool = pool.filter(
    (i) => stageRank(states[i]?.stage) === minStageRank
  );
  if (phasePool.length === 0) {
    phasePool = pool;
  }

  const chosen =
    phasePool[Math.floor(Math.random() * phasePool.length)] ?? null;
  return chosen;
}

// Phrase in Tokens für Tiles aufsplitten; einzelne Satzzeichen (französisch „ça va ?“)
// hängen am vorigen Wort statt als eigenes Kärtchen
function splitPhraseToTokens(phrase) {
  if (!phrase) return [];
  const tokens = [];
  for (const t of phrase.split(" ").map((x) => x.trim()).filter((x) => x.length > 0)) {
    if (/^[?!:;»]+$/.test(t) && tokens.length > 0) tokens[tokens.length - 1] += t;
    else tokens.push(t);
  }
  return tokens;
}

// Übung „Redewendungen“: in den ersten Wochen Themen (Zahlen, Uhrzeit …)
// im eigenen Screen, danach die Redewendungen (siehe phraseSchedule.js)
export default function PhrasesPractice(props) {
  // festhalten: ein neues Objekt bei jedem Rendern würde die Themen-Woche neu starten
  const weekPlan = useMemo(
    () =>
      getPhraseWeekPlan(
        props.weekIndex || 1,
        props.pace,
        parseLanguageId(props.language || "es-de")?.source
      ),
    [props.weekIndex, props.pace, props.language]
  );
  if (weekPlan.mode === "topic") {
    return <TopicPractice {...props} weekPlan={weekPlan} />;
  }
  return <PhraseWeekPractice {...props} weekPlan={weekPlan} />;
}

function PhraseWeekPractice({
  language,
  pace,
  profileId,
  weekIndex,
  weekPlan,
  onBack,
}) {
  const langId = language || "es-de";
  const learningLang = parseLanguageId(langId)?.source || "es";
  const paceValue = pace || "medium";
  const profile = profileId || "default";
  const currentWeek = weekIndex || 1;

  const [userId, setUserId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [phrases, setPhrases] = useState([]);
  const [phraseStates, setPhraseStates] = useState([]); // pro Phrase: { stage, correct1, correct2, correct3 }
  const [currentIndex, setCurrentIndex] = useState(null);
  const [allDone, setAllDone] = useState(false);

  // aktuelle Stage (1, 2, 3) für die angezeigte Redewendung
  const [phase, setPhase] = useState(1);

  const phrasesPerWeek = getPhrasesPerWeek(paceValue);

  // -------- Phase 1: Multiple Choice --------
  const [phase1Selected, setPhase1Selected] = useState(null);
  const [phase1Checked, setPhase1Checked] = useState(false);
  const [phase1Correct, setPhase1Correct] = useState(false);

  // -------- Phase 2: Tiles --------
  const [phase2Tiles, setPhase2Tiles] = useState([]);
  const [phase2Slots, setPhase2Slots] = useState([]);
  const [phase2Checked, setPhase2Checked] = useState(false);
  const [phase2Correct, setPhase2Correct] = useState(false);
  const [phase2ShowSolution, setPhase2ShowSolution] = useState(false);

  // -------- Phase 3: Freitext --------
  const [phase3Input, setPhase3Input] = useState("");
  const [phase3Checked, setPhase3Checked] = useState(false);
  const [phase3IsCorrect, setPhase3IsCorrect] = useState(false);
  const [phase3ShowSolution, setPhase3ShowSolution] = useState(false);

  // Fortschritt dieser Woche (wird pro Woche in Supabase gespeichert, kein Vorarbeiten)
  // Interpretation: 1.0 = eine Phrase vollständig (3 Phasen) abgeschlossen.
  // Jede Phase gibt 1/3.
  const [, setWeekCorrectCount] = useState(0);

  const hasPhrases = phrases && phrases.length > 0;
  const currentPhrase =
    hasPhrases && currentIndex != null ? phrases[currentIndex] : null;

  // Helper: Fortschritt der Woche in Dritteln erhöhen und speichern
  function addWeekProgress(delta) {
    setWeekCorrectCount((prev) => {
      const newTotal = prev + delta;
      if (userId) {
        (async () => {
          try {
            await savePhraseProgress(
              userId,
              profile,
              langId,
              paceValue,
              newTotal,
              currentWeek
            );
          } catch (e) {
            console.warn("PhrasesPractice: savePhraseProgress failed:", e);
          }
        })();
      }
      return newTotal;
    });
  }

  // User + Phrase-Progress laden
  useEffect(() => {
    let cancelled = false;

    async function initUserAndProgress() {
      try {
        const { data: userData, error: userError } =
          await supabase.auth.getUser();
        if (userError) {
          console.warn("PhrasesPractice: getUser error:", userError);
        }
        const uid = userData?.user?.id || null;
        if (!cancelled) {
          setUserId(uid);
        }

        if (uid) {
          const count = await loadPhraseProgress(
            uid,
            profile,
            langId,
            paceValue,
            currentWeek
          );
          if (!cancelled && typeof count === "number") {
            setWeekCorrectCount(count);
          }
        }
      } catch (e) {
        console.warn("PhrasesPractice: initUserAndProgress failed:", e);
      }
    }

    initUserAndProgress();
    return () => {
      cancelled = true;
    };
  }, [profile, langId, paceValue, currentWeek]);

  // Phrasen der Lernsprache (<lang>_phrases.csv) laden und pro Woche zuteilen
  useEffect(() => {
    let cancelled = false;

    async function loadPhrasesForWeek() {
      setLoading(true);
      setError(null);
      setAllDone(false);

      try {
        const all = await loadPhrases(learningLang);
        if (cancelled) return;

        if (!all || all.length === 0) {
          setError(`Für ${languageLabel(learningLang)} gibt es noch keine Redewendungen.`);
          setPhrases([]);
          setPhraseStates([]);
          setCurrentIndex(null);
          return;
        }

        const total = all.length;
        const totalWeeks = Math.max(
          1,
          Math.ceil(total / (phrasesPerWeek || 1))
        );

        // Woche 1 der Redewendungen = erste Woche nach den Themen-Wochen
        const normalizedWeekIndex =
          ((weekPlan.phraseWeek - 1) % totalWeeks) + 1;

        const startIndex = (normalizedWeekIndex - 1) * phrasesPerWeek;
        const endIndex = startIndex + phrasesPerWeek;
        let weeklySlice = all.slice(startIndex, endIndex);

        if (weeklySlice.length === 0) {
          weeklySlice = all.slice(-phrasesPerWeek);
        }

        const shuffledWeekly = shuffle(weeklySlice);

        const initialStates = shuffledWeekly.map(() => ({
          stage: 1,
          correct1: 0,
          correct2: 0,
          correct3: 0,
        }));

        const firstIndex = pickNextPhraseIndex(initialStates, null);

        if (!cancelled) {
          setPhrases(shuffledWeekly);
          setPhraseStates(initialStates);
          setCurrentIndex(firstIndex);
          if (firstIndex !== null) {
            const st = initialStates[firstIndex]?.stage || 1;
            setPhase(st);
            resetUIForStageAndPhrase(st, shuffledWeekly[firstIndex]);
          } else {
            setPhase(1);
          }
        }
      } catch (e) {
        console.warn("PhrasesPractice: loadPhrasesForWeek failed:", e);
        if (!cancelled) {
          setError(
            e?.message || "Unbekannter Fehler beim Laden der Redewendungen."
          );
          setPhrases([]);
          setPhraseStates([]);
          setCurrentIndex(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPhrasesForWeek();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeek, paceValue, weekPlan.phraseWeek, learningLang]);

  // -------- UI-Reset-Helper --------

  function resetPhase1() {
    setPhase1Selected(null);
    setPhase1Checked(false);
    setPhase1Correct(false);
  }

  function initPhase2ForPhrase(phrase) {
    const tokens = splitPhraseToTokens(phrase?.target || "");
    const tiles = shuffle(tokens);
    setPhase2Tiles(tiles);
    setPhase2Slots(tokens.map(() => null));
    setPhase2Checked(false);
    setPhase2Correct(false);
    setPhase2ShowSolution(false);
  }

  function resetPhase2ForPhrase(phrase) {
    if (!phrase) {
      setPhase2Tiles([]);
      setPhase2Slots([]);
      setPhase2Checked(false);
      setPhase2Correct(false);
      setPhase2ShowSolution(false);
      return;
    }
    initPhase2ForPhrase(phrase);
  }

  function resetPhase3() {
    setPhase3Input("");
    setPhase3Checked(false);
    setPhase3IsCorrect(false);
    setPhase3ShowSolution(false);
  }

  function resetUIForStageAndPhrase(stage, phrase) {
    resetPhase1();
    resetPhase3();
    if (stage === 2) {
      resetPhase2ForPhrase(phrase);
    } else {
      setPhase2Tiles([]);
      setPhase2Slots([]);
      setPhase2Checked(false);
      setPhase2Correct(false);
      setPhase2ShowSolution(false);
    }
  }

  // -------- Multiple Choice Optionen (Phase 1) --------

  const phase1Options = useMemo(() => {
    if (!currentPhrase || !hasPhrases) return [];
    const correct = currentPhrase.target;

    const others = phrases
      .filter((p, idx) => idx !== currentIndex && p?.target)
      .map((p) => p.target);

    const distractors = shuffle(others).slice(0, 3);
    const allOptions = shuffle([correct, ...distractors]);
    return allOptions;
  }, [currentPhrase, phrases, currentIndex, hasPhrases]);

  // -------- Phase 1: Check + Next --------

  const handlePhase1Check = () => {
    if (!currentPhrase || phase1Selected == null) return;
    const expected = normalizePhrase(currentPhrase.target);
    const chosen = normalizePhrase(phase1Selected);
    const ok = expected.length > 0 && expected === chosen;
    setPhase1Checked(true);
    setPhase1Correct(ok);
  };

  const handlePhase1Next = () => {
    if (!hasPhrases || currentIndex == null) return;

    const newStates = [...phraseStates];
    const currentState =
      newStates[currentIndex] || {
        stage: 1,
        correct1: 0,
        correct2: 0,
        correct3: 0,
      };

    let unlockedStage2 = false;

    if (phase1Correct) {
      const newCorrect1 = (currentState.correct1 || 0) + 1;
      currentState.correct1 = newCorrect1;
      // Phase 1 -> 2: erstes Drittel vergeben
      if (currentState.stage === 1 && newCorrect1 >= 2) {
        currentState.stage = 2; // Phase 2 freischalten
        unlockedStage2 = true;
      }
    }

    newStates[currentIndex] = currentState;
    setPhraseStates(newStates);

    if (unlockedStage2) {
      addWeekProgress(1 / 3);
    }

    const nextIndex = pickNextPhraseIndex(newStates, currentIndex);
    if (nextIndex === null) {
      setAllDone(true);
      setCurrentIndex(null);
      return;
    }

    const nextStage = newStates[nextIndex]?.stage || 1;
    setPhase(nextStage);
    setCurrentIndex(nextIndex);
    resetUIForStageAndPhrase(nextStage, phrases[nextIndex]);
  };

  // -------- Phase 2: Tiles-Interaktion --------

  const handlePhase2TileClick = (tile) => {
    if (phase2Checked) {
      setPhase2Checked(false);
      setPhase2Correct(false);
      setPhase2ShowSolution(false);
    }

    const idx = phase2Slots.findIndex((s) => s === null);
    if (idx === -1) return;

    const newSlots = [...phase2Slots];
    newSlots[idx] = tile;

    const newTiles = [...phase2Tiles];
    const tileIdx = newTiles.indexOf(tile);
    if (tileIdx !== -1) {
      newTiles.splice(tileIdx, 1);
    }

    setPhase2Slots(newSlots);
    setPhase2Tiles(newTiles);
  };

  const handlePhase2SlotClick = (index) => {
    const value = phase2Slots[index];
    if (!value) return;

    if (phase2Checked) {
      setPhase2Checked(false);
      setPhase2Correct(false);
      setPhase2ShowSolution(false);
    }

    const newSlots = [...phase2Slots];
    newSlots[index] = null;

    const newTiles = [...phase2Tiles, value];

    setPhase2Slots(newSlots);
    setPhase2Tiles(newTiles);
  };

  const handlePhase2Check = () => {
    if (!currentPhrase) return;

    const expected = normalizePhrase(currentPhrase.target);
    const joined = phase2Slots.filter(Boolean).join(" ");
    const given = normalizePhrase(joined);

    const ok =
      expected.length > 0 &&
      given.length > 0 &&
      expected === given &&
      !phase2Slots.includes(null);

    setPhase2Checked(true);
    setPhase2Correct(ok);
    setPhase2ShowSolution(!ok);
  };

  const handlePhase2Next = () => {
    if (!hasPhrases || currentIndex == null) return;

    const newStates = [...phraseStates];
    const currentState =
      newStates[currentIndex] || {
        stage: 1,
        correct1: 0,
        correct2: 0,
        correct3: 0,
      };

    let unlockedStage3 = false;

    if (phase2Correct) {
      const newCorrect2 = (currentState.correct2 || 0) + 1;
      currentState.correct2 = newCorrect2;
      // Phase 2 -> 3: zweites Drittel vergeben
      if (currentState.stage === 2 && newCorrect2 >= 2) {
        currentState.stage = 3; // Phase 3 freischalten
        unlockedStage3 = true;
      }
    }

    newStates[currentIndex] = currentState;
    setPhraseStates(newStates);

    if (unlockedStage3) {
      addWeekProgress(1 / 3);
    }

    const nextIndex = pickNextPhraseIndex(newStates, currentIndex);
    if (nextIndex === null) {
      setAllDone(true);
      setCurrentIndex(null);
      return;
    }

    const nextStage = newStates[nextIndex]?.stage || 1;
    setPhase(nextStage);
    setCurrentIndex(nextIndex);
    resetUIForStageAndPhrase(nextStage, phrases[nextIndex]);
  };

  // -------- Phase 3: Freitext --------

  const handlePhase3Check = async () => {
    if (!currentPhrase) return;

    const expected = normalizePhrase(currentPhrase.target);
    const given = normalizePhrase(phase3Input);

    const ok = expected.length > 0 && expected === given;

    setPhase3Checked(true);
    setPhase3IsCorrect(ok);
    setPhase3ShowSolution(!ok);

    if (!hasPhrases || currentIndex == null) return;

    const newStates = [...phraseStates];
    const currentState =
      newStates[currentIndex] || {
        stage: 1,
        correct1: 0,
        correct2: 0,
        correct3: 0,
      };

    let finishedPhraseNow = false;

    if (ok) {
      const newCorrect3 = (currentState.correct3 || 0) + 1;
      currentState.correct3 = newCorrect3;

      // Phase 3 -> done: drittes Drittel vergeben
      if (currentState.stage === 3 && newCorrect3 >= 2) {
        currentState.stage = "done";
        finishedPhraseNow = true;
      }
    }

    newStates[currentIndex] = currentState;
    setPhraseStates(newStates);

    if (finishedPhraseNow) {
      addWeekProgress(1 / 3);
    }
  };

  const handlePhase3NextPhrase = () => {
    if (!hasPhrases || currentIndex == null) return;

    const nextIndex = pickNextPhraseIndex(phraseStates, currentIndex);
    if (nextIndex === null) {
      setAllDone(true);
      setCurrentIndex(null);
      return;
    }

    const nextStage = phraseStates[nextIndex]?.stage || 1;
    setPhase(nextStage);
    setCurrentIndex(nextIndex);
    resetUIForStageAndPhrase(nextStage, phrases[nextIndex]);
  };

  const handlePhase3InputChange = (e) => {
    setPhase3Input(e.target.value);
    if (phase3Checked) {
      setPhase3Checked(false);
      setPhase3IsCorrect(false);
      setPhase3ShowSolution(false);
    }
  };

  const title = `Redewendungen – Woche ${currentWeek}`;
  const backButton = (
    <Button block variant="secondary" onClick={onBack}>
      Zurück zum Hauptmenü
    </Button>
  );

  if (loading) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>fluent! lädt deine Redewendungen …</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note tone="error">Fehler: {error}</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (allDone) {
    return (
      <>
        <ScreenHeader
          title="Alle Redewendungen dieser Woche abgeschlossen 🎉"
          subtitle="Du hast jede Redewendung mindestens zweimal in allen drei Phasen richtig gelöst. Super Arbeit!"
        />
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (!hasPhrases || !currentPhrase || currentIndex == null) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>Keine Redewendungen für diese Woche gefunden.</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  // Überschrift je nach Phase
  let headingText = null;
  if (phase === 1) {
    headingText = "Wähle die richtige Übersetzung aus";
  } else if (phase === 2) {
    headingText = "Bringe die Karten in die richtige Reihenfolge";
  } else if (phase === 3) {
    headingText = "Übersetze die Phrase";
  }

  return (
    <>
      <ScreenHeader title={title} subtitle={headingText} />

      {/* Deutsche Bedeutung (immer oben gleich) */}
      <Panel variant="muted" className="exercise-prompt">
        <Badge tone="blue">Phase {phase} von 3</Badge>
        <div className="exercise-prompt__text">{currentPhrase.src}</div>
      </Panel>

      {/* PHASE 1: Multiple Choice */}
      {phase === 1 && (
        <Stack gap={4}>
          <Stack gap={2}>
            {phase1Options.map((opt) => {
              const isSelected = phase1Selected === opt;
              const isCorrectOption =
                phase1Checked &&
                normalizePhrase(opt) === normalizePhrase(currentPhrase.target);
              const isWrongSelected =
                phase1Checked &&
                isSelected &&
                normalizePhrase(opt) !== normalizePhrase(currentPhrase.target);

              return (
                <OptionCard
                  key={opt}
                  label={opt}
                  active={isSelected}
                  tone={isCorrectOption ? "right" : isWrongSelected ? "wrong" : undefined}
                  onClick={() => {
                    setPhase1Selected(opt);
                    if (phase1Checked) {
                      setPhase1Checked(false);
                      setPhase1Correct(false);
                    }
                  }}
                />
              );
            })}
          </Stack>

          {!phase1Checked ? (
            <Button block onClick={handlePhase1Check} disabled={phase1Selected == null}>
              Auswahl prüfen
            </Button>
          ) : (
            <Button block onClick={handlePhase1Next}>
              Nächste Aufgabe
            </Button>
          )}
        </Stack>
      )}

      {/* PHASE 2: Tiles */}
      {phase === 2 && (
        <Stack gap={3}>
          {/* Slots */}
          <TileTray variant="answer">
            {phase2Slots.map((slot, index) => (
              <Tile
                key={`slot-${index}`}
                empty={!slot}
                onClick={() => handlePhase2SlotClick(index)}
              >
                {slot}
              </Tile>
            ))}
          </TileTray>

          {/* Tiles */}
          <TileTray variant="pool" empty="Alle Kärtchen verwendet – prüfe deine Lösung.">
            {phase2Tiles.map((tile, index) => (
              <Tile
                key={`tile-${tile}-${index}`}
                onClick={() => handlePhase2TileClick(tile)}
              >
                {tile}
              </Tile>
            ))}
          </TileTray>

          {/* Feedback */}
          {phase2Checked && (
            <Note tone={phase2Correct ? "success" : "error"} className="exercise-feedback">
              {phase2Correct
                ? "Sehr gut! Die Reihenfolge stimmt. 🎉"
                : "Nicht ganz – sie kommt später nochmal dran."}
            </Note>
          )}

          {/* Lösung anzeigen, falls falsch */}
          {phase2ShowSolution && (
            <Note className="exercise-feedback">
              Richtige Reihenfolge: <strong>{currentPhrase.target}</strong>
            </Note>
          )}

          {!phase2Checked ? (
            <Button block onClick={handlePhase2Check}>
              Lösung prüfen
            </Button>
          ) : (
            <Button block onClick={handlePhase2Next}>
              Nächste Aufgabe
            </Button>
          )}
        </Stack>
      )}

      {/* PHASE 3: Freitext */}
      {phase === 3 && (
        <Stack gap={3}>
          <div>
            <label className="ui-label" htmlFor="phrase-input">
              Schreibe die Redewendung auf {languageLabel(learningLang)}:
            </label>
            <TextInput
              id="phrase-input"
              type="text"
              value={phase3Input}
              onChange={handlePhase3InputChange}
              autoComplete="off"
              spellCheck="false"
              status={phase3Checked ? (phase3IsCorrect ? "correct" : "wrong") : undefined}
            />
          </div>

          {/* Feedback */}
          {phase3Checked && (
            <Note tone={phase3IsCorrect ? "success" : "error"} className="exercise-feedback">
              {phase3IsCorrect ? "Richtig! 🎉" : "Fast – schau dir die Lösung an."}
            </Note>
          )}

          {/* Lösung anzeigen, falls falsch */}
          {phase3ShowSolution && (
            <Note className="exercise-feedback">
              Richtige Redewendung: <strong>{currentPhrase.target}</strong>
            </Note>
          )}

          {!phase3Checked ? (
            <Button block onClick={handlePhase3Check}>
              Lösung prüfen
            </Button>
          ) : (
            <Button block onClick={handlePhase3NextPhrase}>
              Nächste Aufgabe
            </Button>
          )}
        </Stack>
      )}

      <div className="exercise-actions-tight">{backButton}</div>
    </>
  );
}
